import React, { useState, useEffect } from "react";
import { ApolloClient, InMemoryCache, ApolloProvider, createHttpLink, from } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import { envKeys, allowedEnvs, DEFAULT_TENANT_ID } from "./utils/env_config";
import {
  GET_TENANTS,
  GET_USERS_FOR_FOUNDRY,
  GET_MEASUREMENT_TECHNOLOGY,
  GET_PROJECT_GROUPS,
  GET_COUNTRIES,
  GET_GROUP_ASSET_TYPES,
  GET_GROUP_VERSIONS,
  GET_OGMP_CATEGORIES,
  GET_GROUP_VERSION_OGMP_CATEGORIES,
  GET_SUBSCRIPTIONS,
  GET_SITE_MEASUREMENTS_BY_PROJECT_GROUP_VERSION,
  GET_ELIGIBLE_SITES_FOR_GROUP_VERSION
} from "./graphql/queries";
import { getValidToken } from "./utils/auth0";
import { endSession } from "./utils/session";
import { appendUserToLists, filterUsersList } from "./utils/usersList";
import TenantsListPage from "./pages/Tenants/TenantsListPage";
import UsersListPage from "./pages/Users/UsersListPage";
import ProjectGroupsListPage from "./pages/Projects/ProjectGroups/ProjectsGroupsListPage";
import GroupVersionsListPage from "./pages/Projects/GroupVersions/GroupVersionsListPage";
import MeasurementTechListPage from "./pages/Measurements/MeasurementTech/MeasurementTechListPage";
import SiteMeasurementsListPage from "./pages/Measurements/SiteMeasurement/SiteMeasurementsListPage";
import VersionsCategoriesListPage from "./pages/Projects/VersionCategories/VersionsCategoriesListPage";
import FlatTableRefreshPage from "./pages/System/FlatTableRefreshPage";

import { Header, SubHeader } from "./components";
import { AppProvider } from "./context/appContext";
import LogInPage from "./pages/LogIn/LogInPage";
import ProtectedRoute from "./components/ProtectedRoute";
import ProtectedEditRoute from "./components/ProtectedEditRoute";
import ProtectedPrivilegedRoute from "./components/ProtectedPrivilegedRoute";
import AddTenantPage from "./pages/Tenants/AddTenant/AddTenantPage";
import { EditTenantPage } from "./pages/Tenants/UpdateTenant";
import AddUserPage from "./pages/Users/AddUser/AddUserPage";
import AddGroupVersionPage from "./pages/Projects/GroupVersions/AddGroupVersion/AddGroupVersionPage";
import AddProjectGroupPage from "./pages/Projects/ProjectGroups/AddProjectGroup/AddProjectGroupPage";
import AddSiteMeasurementPage from "./pages/Measurements/SiteMeasurement/AddSiteMeasurement/AddSiteMeasurementPage";
import IdleTimerContainer from "./components/IdleTimer";

const App = () => {
  const user = localStorage.getItem("user");
  const savedEnv = user ? localStorage.getItem("currentEnv") || "dev" : null;
  const savedTenant = localStorage.getItem("currentTenant") || DEFAULT_TENANT_ID;
  const savedGroupVersion = localStorage.getItem("currentGroupVersion") || null;

  const [env, setEnv] = useState(savedEnv);
  const [tenantsData, setTenantData] = useState(null);
  const [usersData, setUsersData] = useState(null);
  const [projectGroupsData, setProjectGroupsData] = useState(null);
  const [measurementTechData, setMeasurementTechData] = useState(null);
  const [countriesData, setCountriesData] = useState(null);
  const [groupAssetTypesData, setGroupAssetTypesData] = useState(null);
  const [groupVersionsData, setGroupVersionsData] = useState(null);
  const [versionsCategoriesData, setVersionsCategoriesData] = useState(null);
  const [ogmpCategoriesData, setOgmpCategoriesData] = useState(null);
  const [siteNamesData, setSiteNamesData] = useState(null);
  const [currentTenant, setCurrentTenant] = useState(savedTenant);
  const [currentGroupVersion, setCurrentGroupVersion] = useState(savedGroupVersion);
  const [selectedTenantName, setSelectedTenantName] = useState(savedTenant);
  const [filteredTenants, setFilteredTenants] = useState(null);
  const [filteredUsers, setFilteredUsers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [countriesError, setCountriesError] = useState(null);
  const [assetTypesLoading, setAssetTypesLoading] = useState(false);
  const [assetTypesError, setAssetTypesError] = useState(null);
  const [apolloClient, setApolloClient] = useState(null);
  const [pageResetFlag, setPageResetFlag] = useState(false);
  const [subscriptions, setSubscriptions] = useState([]);
  const [siteMeasurementsData, setSiteMeasurementsData] = useState([]);
  const [selectedTenantIds, setSelectedTenantIds] = useState([]);
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false);

  const createApolloClient = (uri, apiKey, selectedEnv) => {
    const httpLink = createHttpLink({
      uri
    });

    // The backend requires both credentials on every request. `x-api-key` is deliberately
    // not sent: AppSync resolves any request carrying it in API_KEY mode and never invokes
    // the Lambda authorizer, so the token would be ignored and every non-exempt field
    // would come back Unauthorized.
    //
    // The token is read per request rather than captured here, so one refreshed
    // mid-session is picked up without rebuilding the client.
    const authLink = setContext(async (_, { headers }) => {
      const token = await getValidToken(selectedEnv);

      return {
        headers: {
          ...headers,
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "x-evn-api-key": apiKey
        }
      };
    });

    const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
      graphQLErrors?.forEach((err) => {
        const errorType = err.errorType ?? err.extensions?.errorType;
        console.error(`GraphQL error on ${operation.operationName}: ${err.message}` + (errorType ? ` [errorType=${errorType}]` : ""));
      });

      if (networkError) {
        console.error(`Network error on ${operation.operationName}: ${networkError.message}`);
      }

      const unauthorized = graphQLErrors?.some((err) => err.errorType === "Unauthorized" || err.extensions?.errorType === "Unauthorized");

      if (unauthorized) {
        endSession("Your session has expired. Please sign in again.");
      }
    });

    return new ApolloClient({
      link: from([errorLink, authLink, httpLink]),
      cache: new InMemoryCache()
    });
  };

  const initializeClientAndState = async (selectedEnv, shouldResetData = true) => {
    setEnv(selectedEnv);
    localStorage.setItem("currentEnv", selectedEnv);

    setLoading(true);
    setError(null);

    if (shouldResetData) {
      setTenantData(null);
      setUsersData(null);
      setMeasurementTechData(null);
      setCountriesData(null);
      setGroupAssetTypesData(null);
      setVersionsCategoriesData(null);
      setOgmpCategoriesData(null);
      setSiteNamesData(null);
      setSiteMeasurementsData(null);
      setSelectedTenantIds([]);
      setShowBulkAssignModal(false);
    }

    const apiKey = envKeys[selectedEnv];
    const envConfig = allowedEnvs[selectedEnv];

    // Only a signed-in session needs a token; the login page itself issues no queries.
    // dev/qa and uat/prod are separate Auth0 tenants, so crossing between them mints a
    // new token from the credentials still held in memory. After a page reload that
    // memory is gone, and the only way forward is to sign in again.
    if (localStorage.getItem("user")) {
      const token = await getValidToken(selectedEnv);

      if (!token) {
        endSession("Please sign in again to switch environments.");
        return null;
      }
    }

    const newClient = createApolloClient(envConfig.uri, apiKey, selectedEnv);
    setApolloClient(newClient);

    return newClient;
  };

  const handleEnvChange = async (selectedEnv, selectedTenant, selectedTenantName, selectedGroupVersionId) => {
    let selectedTenantID = selectedTenant ?? DEFAULT_TENANT_ID;

    const user = localStorage.getItem("user");

    if (!user) {
      setEnv(null);
      initEnv("dev");
      return;
    }

    const envChanged = selectedEnv !== env || !env || env === "";

    const currentPage = localStorage.getItem("activeKey") || "/tenants";
    if (envChanged && currentPage === "/users") {
      selectedTenantID = DEFAULT_TENANT_ID;
      selectedTenantName = null;
      selectedGroupVersionId = null;
    }

    localStorage.setItem("currentTenant", selectedTenantID);
    localStorage.setItem("selectedTenantName", selectedTenantName);
    localStorage.setItem("currentGroupVersion", selectedGroupVersionId);

    const newClient = await initializeClientAndState(selectedEnv, envChanged);

    // No client means the session ended and a reload onto the login page is already
    // under way; there is nothing left to query.
    if (!newClient) {
      return;
    }

    const currentTenantName =
      selectedTenantName && selectedTenantName !== "null" && selectedTenantName !== "undefined" ? selectedTenantName : null;

    try {
      setLoading(true);

      // Define all base queries that will run in parallel
      const queryFunctions = [
        // tenants query - always runs
        newClient.query({ query: GET_TENANTS }),

        // Project Groups query - always runs
        newClient
          .query({
            query: GET_PROJECT_GROUPS,
            variables: {
              tenantId: selectedTenantID
            }
          })
          .catch((err) => {
            console.error("Project Groups data could not be loaded:", err.message);
            return { data: { projectGroups: null } };
          }),

        // Measurement Technology query - always runs
        newClient
          .query({
            query: GET_MEASUREMENT_TECHNOLOGY,
            variables: {
              tenantId: selectedTenantID
            }
          })
          .catch((err) => {
            console.error("Measurement technology data could not be loaded:", err.message);
            return { data: { getMeasurementTechnologies: null } };
          }),

        // Countries query - always runs
        (() => {
          setCountriesLoading(true);
          return newClient
            .query({
              query: GET_COUNTRIES
            })
            .catch((err) => {
              console.error("Countries data could not be loaded:", err.message);
              setCountriesError(err.message);
              return { data: { countries: null } };
            })
            .finally(() => {
              setCountriesLoading(false);
            });
        })(),

        // Asset Types query - always runs
        (() => {
          setAssetTypesLoading(true);
          return newClient
            .query({
              query: GET_GROUP_ASSET_TYPES
            })
            .catch((err) => {
              console.error("Group asset types data could not be loaded:", err.message);
              setAssetTypesError(err.message);
              return { data: { groupAssetTypes: null } };
            })
            .finally(() => {
              setAssetTypesLoading(false);
            });
        })(),

        // Subscriptions query - always runs

        (() => {
          return newClient
            .query({
              query: GET_SUBSCRIPTIONS
            })
            .catch((err) => {
              console.error("Subscriptions data could not be loaded:", err.message);
            });
        })()
      ];

      if (envChanged || !usersData) {
        queryFunctions.push(
          newClient.query({
            query: GET_USERS_FOR_FOUNDRY
          })
        );
      }

      if (currentTenantName) {
        queryFunctions.push(
          newClient
            .query({
              query: GET_GROUP_VERSIONS,
              variables: {
                tenantId: selectedTenantID
              }
            })
            .catch((err) => {
              console.error("Group Versions data could not be loaded:", err.message);
              return { data: { groupVersions: null } };
            })
        );

        // OGMP Categories query
        queryFunctions.push(
          newClient
            .query({
              query: GET_OGMP_CATEGORIES,
              variables: {
                tenantId: selectedTenantID,
                isOgmp: true
              }
            })
            .catch((err) => {
              console.error("OGMP Categories data could not be loaded:", err.message);
              return { data: { tenantSourceCategories: null } };
            })
        );

        // Versions OGMP Categories query - runs if tenant name and group version ID exist
        if (selectedGroupVersionId) {
          queryFunctions.push(
            newClient
              .query({
                query: GET_GROUP_VERSION_OGMP_CATEGORIES,
                variables: {
                  groupVersionId: selectedGroupVersionId
                }
              })
              .catch((err) => {
                console.error("Versions OGMP Category data could not be loaded:", err.message);
                return { data: { getGroupVersionOGMPCategories: null } };
              })
          );
        }

        // Site Measurements query / eligibleSitesForGroupVersion - runs if tenant name and group version ID exist
        if (selectedGroupVersionId) {
          queryFunctions.push(
            newClient
              .query({
                query: GET_SITE_MEASUREMENTS_BY_PROJECT_GROUP_VERSION,
                variables: {
                  tenantId: selectedTenantID,
                  projectGroupVersionId: selectedGroupVersionId
                }
              })
              .catch((err) => {
                console.error("Site Measurements data could not be loaded:", err.message);
                return { data: { getGroupVersionOGMPCategories: null } };
              })
          );

          queryFunctions.push(
            newClient
              .query({
                query: GET_ELIGIBLE_SITES_FOR_GROUP_VERSION,
                variables: {
                  groupVersionId: selectedGroupVersionId
                }
              })
              .catch((err) => {
                console.error("Sites for GroupVersion data could not be loaded:", err.message);
                return { data: { eligibleSitesForGroupVersion: null } };
              })
          );
        }
      }

      // Execute all queries in parallel
      const results = await Promise.all(queryFunctions);

      // Extract results based on array position
      const tenantsResult = results[0];
      const projectGroupsResult = results[1];
      const measurementTechResult = results[2];
      const countriesResult = results[3];
      const assetTypesResult = results[4];
      const subscriptionsResult = results[5];

      let resultIndex = 6;
      let usersResult = null;

      if (envChanged || !usersData) {
        usersResult = results[resultIndex];
        resultIndex++;
      }

      let groupVersionsResult = { data: { groupVersions: null } };
      let ogmpCategoriesResult = { data: { tenantSourceCategories: null } };
      let versionsOgmpCategoriesResult = { data: { getGroupVersionOGMPCategories: null } };
      let siteMeasurementsResult = { data: { getSiteMeasurementsByProjectGroupVersion: null } };
      let sitesForGroupVersionResult = { data: { eligibleSitesForGroupVersion: null } };

      if (currentTenantName) {
        groupVersionsResult = results[resultIndex];
        ogmpCategoriesResult = results[resultIndex + 1];
        resultIndex += 2;

        if (selectedGroupVersionId) {
          versionsOgmpCategoriesResult = results[resultIndex];
          siteMeasurementsResult = results[resultIndex + 1];
          sitesForGroupVersionResult = results[resultIndex + 2];
        }
      }

      setTenantData(tenantsResult.data.tenants);
      setProjectGroupsData(projectGroupsResult.data.projectGroups);
      setMeasurementTechData(measurementTechResult.data.getMeasurementTechnologies);
      setCountriesData(countriesResult.data.countries);
      setGroupAssetTypesData(assetTypesResult.data.groupAssetTypes);
      setSubscriptions(subscriptionsResult.data.appSubscriptions);

      if (usersResult) {
        const allUsers = usersResult.data.usersForFoundry.items.flatMap((item) =>
          item.users.map((user) => ({
            ...user,
            tenantName: item.tenantName,
            tenantId: item.tenantId
          }))
        );

        setUsersData(allUsers);

        const savedUsersSearch = localStorage.getItem("usersSearchTerm") || "";
        const savedUsersSelectedTenant = localStorage.getItem("usersSelectedTenant") || null;

        setFilteredUsers(
          filterUsersList(allUsers, {
            searchTerm: savedUsersSearch,
            tenantId: savedUsersSelectedTenant
          })
        );
      } else {
        reapplySavedUsersFilters();
      }

      if (currentTenantName) {
        setGroupVersionsData(groupVersionsResult.data.groupVersions);
        setOgmpCategoriesData(ogmpCategoriesResult.data.tenantSourceCategories);

        if (selectedGroupVersionId) {
          setVersionsCategoriesData(versionsOgmpCategoriesResult.data.getGroupVersionOGMPCategories);
          setSiteMeasurementsData(siteMeasurementsResult.data.getSiteMeasurementsByProjectGroupVersion);
          setSiteNamesData(sitesForGroupVersionResult.data.eligibleSitesForGroupVersion);
        }
      }

      // set remaining state variables
      setCurrentGroupVersion(selectedGroupVersionId);
      setCurrentTenant(selectedTenantID);
      setSelectedTenantName(selectedTenantName);

      // Apply saved tenant search filter if exists
      const savedTenantsSearch = localStorage.getItem("tenantsSearchTerm") || "";
      if (savedTenantsSearch) {
        const filtered = tenantsResult.data.tenants.filter(
          (tenant) =>
            tenant?.name?.toLowerCase().includes(savedTenantsSearch.toLowerCase()) ||
            tenant?.description?.toLowerCase().includes(savedTenantsSearch.toLowerCase())
        );
        setFilteredTenants(filtered);
      } else {
        setFilteredTenants(tenantsResult.data.tenants);
      }

      setPageResetFlag((prev) => !prev);
    } catch (err) {
      setError(err.message);
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const initEnv = async (selectedEnv) => {
    await initializeClientAndState(selectedEnv);
    setLoading(false);
  };

  const handleSearch = (searchTerm) => {
    if (tenantsData) {
      const filteredTenants = tenantsData.filter(
        (tenant) =>
          tenant?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          tenant?.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );

      setFilteredTenants(filteredTenants);
    }
  };

  const handleTenantFilter = (tenantId) => {
    if (usersData) {
      setFilteredUsers(filterUsersList(usersData, { tenantId }));
    }
  };

  const reapplySavedUsersFilters = () => {
    const searchTerm = localStorage.getItem("usersSearchTerm") || "";
    const tenantId = localStorage.getItem("usersSelectedTenant") || null;

    setUsersData((currentUsersData) => {
      if (currentUsersData) {
        setFilteredUsers(filterUsersList(currentUsersData, { searchTerm, tenantId }));
      }
      return currentUsersData;
    });
  };

  const handleUsersFilter = (searchTerm, tenantId) => {
    if (usersData) {
      setFilteredUsers(filterUsersList(usersData, { searchTerm, tenantId }));
    }
  };

  const resetUsersFilters = () => {
    if (usersData) {
      setFilteredUsers(usersData);
    }
  };

  const handleUserUpdate = (updatedUserData) => {
    if (usersData && filteredUsers) {
      // Update usersData
      const updatedUsersData = usersData.map((user) =>
        user.email === updatedUserData.email
          ? {
              ...user,
              firstName: updatedUserData.firstName,
              lastName: updatedUserData.lastName,
              tenantName: updatedUserData.tenantName,
              tenantId: updatedUserData.tenantId,
              isInactive: updatedUserData.isInactive,
              role: { ...user.role, name: updatedUserData.roleName }
            }
          : user
      );
      setUsersData(updatedUsersData);

      const updatedUser = updatedUsersData.find((user) => user.email === updatedUserData.email);
      const userExistsInFiltered = filteredUsers.some((user) => user.email === updatedUserData.email);
      const hasFilters = filteredUsers.length !== usersData.length;

      if (!hasFilters) {
        setFilteredUsers(updatedUsersData);
      } else {
        if (userExistsInFiltered) {
          const updatedFilteredUsers = filteredUsers.map((user) => (user.email === updatedUserData.email ? updatedUser : user));
          setFilteredUsers(updatedFilteredUsers);
        }
      }
    }
  };

  const handleUserDelete = (deletedUserEmail) => {
    if (usersData) {
      const updatedUsersData = usersData.filter((user) => user.email !== deletedUserEmail);
      setUsersData(updatedUsersData);
    }
    if (filteredUsers) {
      const updatedFilteredUsers = filteredUsers.filter((user) => user.email !== deletedUserEmail);
      setFilteredUsers(updatedFilteredUsers);
    }
  };

  const handleUserCreate = (newUser) => {
    if (!usersData || !filteredUsers) {
      return;
    }

    const searchTerm = localStorage.getItem("usersSearchTerm") || "";
    const tenantId = localStorage.getItem("usersSelectedTenant") || null;
    const { usersData: updatedUsersData, filteredUsers: updatedFilteredUsers } = appendUserToLists(usersData, filteredUsers, newUser, {
      searchTerm,
      tenantId
    });

    setUsersData(updatedUsersData);
    setFilteredUsers(updatedFilteredUsers);
  };

  const resetTenantsFilters = () => {
    if (tenantsData) {
      setFilteredTenants(tenantsData);
    }
  };

  useEffect(() => {
    handleEnvChange(savedEnv, savedTenant);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="bg-light app-container">
      {apolloClient && (
        <AppProvider>
          <ApolloProvider client={apolloClient}>
            <Router>
              <div className="header-container">
                <Header
                  env={env}
                  handleEnvChange={handleEnvChange}
                  handleSearch={handleSearch}
                  handleTenantFilter={handleTenantFilter}
                  handleUsersFilter={handleUsersFilter}
                  resetUsersFilters={resetUsersFilters}
                  resetTenantsFilters={resetTenantsFilters}
                  loading={loading}
                  error={error}
                  tenants={tenantsData}
                  currentTenant={currentTenant}
                  tenantName={selectedTenantName}
                  groupVersions={groupVersionsData}
                />
              </div>
              <div className="subheader-container">
                <SubHeader
                  env={env}
                  loading={loading}
                  handleEnvChange={handleEnvChange}
                  error={error}
                  currentTenant={currentTenant}
                  groups={projectGroupsData}
                  groupVersions={groupVersionsData}
                  currentGroupVersion={currentGroupVersion}
                />
              </div>
              <div className="content-container">
                <IdleTimerContainer />
                <ToastContainer />
                <div className="main-content">
                  <Routes>
                    <Route path="/" element={<LogInPage isLoading={loading} handleEnvChange={handleEnvChange} />} />
                    <Route
                      path="/tenants"
                      element={
                        <ProtectedRoute>
                          <TenantsListPage
                            env={env}
                            tenants={filteredTenants}
                            allTenants={tenantsData}
                            isLoading={loading}
                            isError={error}
                            pageResetFlag={pageResetFlag}
                            subscriptions={subscriptions}
                            handleEnvChange={handleEnvChange}
                            users={filteredUsers}
                            selectedTenantIds={selectedTenantIds}
                            onSelectedTenantIdsChange={setSelectedTenantIds}
                            showBulkAssignModal={showBulkAssignModal}
                            onOpenBulkAssignModal={() => setShowBulkAssignModal(true)}
                            onCloseBulkAssignModal={() => setShowBulkAssignModal(false)}
                          />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/users"
                      element={
                        <ProtectedRoute>
                          <UsersListPage
                            env={env}
                            users={filteredUsers}
                            handleEnvChange={handleEnvChange}
                            isLoading={loading}
                            isError={error}
                            tenants={tenantsData}
                            currentTenant={currentTenant}
                            onUserUpdate={handleUserUpdate}
                            onUserDelete={handleUserDelete}
                          />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/projects"
                      element={
                        <ProtectedRoute>
                          <ProjectGroupsListPage
                            env={env}
                            groups={projectGroupsData}
                            handleEnvChange={handleEnvChange}
                            isLoading={loading}
                            isError={error}
                            currentTenant={currentTenant}
                            assetTypes={groupAssetTypesData}
                            countries={countriesData}
                          />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/group-versions"
                      element={
                        <ProtectedRoute>
                          <GroupVersionsListPage
                            env={env}
                            groupVersions={groupVersionsData}
                            handleEnvChange={handleEnvChange}
                            isLoading={loading}
                            isError={error}
                            currentTenant={currentTenant}
                          />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/version-categories"
                      element={
                        <ProtectedRoute>
                          <VersionsCategoriesListPage
                            env={env}
                            groups={projectGroupsData}
                            groupVersions={groupVersionsData}
                            handleEnvChange={handleEnvChange}
                            isLoading={loading}
                            isError={error}
                            currentTenant={currentTenant}
                            currentGroupVersion={currentGroupVersion}
                            versionsCategories={versionsCategoriesData}
                            ogmpCategories={ogmpCategoriesData}
                            technologies={measurementTechData}
                          />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/measurements"
                      element={
                        <ProtectedRoute>
                          <SiteMeasurementsListPage
                            env={env}
                            groups={projectGroupsData}
                            groupVersions={groupVersionsData}
                            handleEnvChange={handleEnvChange}
                            isLoading={loading}
                            isError={error}
                            currentTenant={currentTenant}
                            currentGroupVersion={currentGroupVersion}
                            siteMeasurements={siteMeasurementsData}
                            pageResetFlag={pageResetFlag}
                            ogmpCategories={ogmpCategoriesData}
                            versionsCategories={versionsCategoriesData}
                          />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/system"
                      element={
                        <ProtectedRoute>
                          <FlatTableRefreshPage env={env} />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/measurement-tech"
                      element={
                        <ProtectedRoute>
                          <MeasurementTechListPage
                            env={env}
                            technologies={measurementTechData}
                            handleEnvChange={handleEnvChange}
                            isLoading={loading}
                            isError={error}
                            pageResetFlag={pageResetFlag}
                            currentTenant={currentTenant}
                          />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/add-tenant"
                      element={
                        <ProtectedEditRoute>
                          <AddTenantPage env={env} handleEnvChange={handleEnvChange} subscriptions={subscriptions} />
                        </ProtectedEditRoute>
                      }
                    />
                    <Route
                      path="/edit-tenant/:tenantId"
                      element={
                        <ProtectedEditRoute>
                          <EditTenantPage env={env} handleEnvChange={handleEnvChange} subscriptions={subscriptions} tenants={tenantsData} />
                        </ProtectedEditRoute>
                      }
                    />
                    <Route
                      path="/view-tenant/:tenantId"
                      element={
                        <ProtectedRoute>
                          <EditTenantPage
                            env={env}
                            handleEnvChange={handleEnvChange}
                            subscriptions={subscriptions}
                            tenants={tenantsData}
                            viewOnly
                          />
                        </ProtectedRoute>
                      }
                    />
                    <Route
                      path="/add-user"
                      element={
                        <ProtectedPrivilegedRoute>
                          <AddUserPage
                            tenants={tenantsData}
                            isLoading={loading}
                            isError={error}
                            env={env}
                            handleEnvChange={handleEnvChange}
                            onUserCreate={handleUserCreate}
                          />
                        </ProtectedPrivilegedRoute>
                      }
                    />
                    <Route
                      path="/add-project-group"
                      element={
                        <ProtectedPrivilegedRoute>
                          <AddProjectGroupPage
                            env={env}
                            currentTenant={currentTenant}
                            tenants={tenantsData}
                            selectedTenant={selectedTenantName}
                            handleEnvChange={handleEnvChange}
                            countries={countriesData}
                            countriesLoading={countriesLoading}
                            countriesError={countriesError}
                            groupAssetTypes={groupAssetTypesData}
                            groupAssetTypesLoading={assetTypesLoading}
                            groupAssetTypesError={assetTypesError}
                            groups={projectGroupsData}
                          />
                        </ProtectedPrivilegedRoute>
                      }
                    />
                    <Route
                      path="/add-group-version"
                      element={
                        <ProtectedPrivilegedRoute>
                          <AddGroupVersionPage
                            env={env}
                            handleEnvChange={handleEnvChange}
                            isLoading={loading}
                            isError={error}
                            currentTenant={currentTenant}
                            tenantName={selectedTenantName}
                            groups={projectGroupsData}
                          />
                        </ProtectedPrivilegedRoute>
                      }
                    />
                    <Route
                      path="/add-site-measurement"
                      element={
                        <ProtectedPrivilegedRoute>
                          <AddSiteMeasurementPage
                            env={env}
                            handleEnvChange={handleEnvChange}
                            isLoading={loading}
                            isError={error}
                            versionsCategories={versionsCategoriesData}
                            ogmpCategories={ogmpCategoriesData}
                            measurementTech={measurementTechData}
                            siteNames={siteNamesData}
                            currentGroupVersion={currentGroupVersion}
                            currentTenant={currentTenant}
                          />
                        </ProtectedPrivilegedRoute>
                      }
                    />
                  </Routes>
                </div>
              </div>
            </Router>
          </ApolloProvider>
        </AppProvider>
      )}
    </div>
  );
};

export default App;
